import {
  Avatar,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { ChangeEvent, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { User } from "types/user.type";
import authStore from "stores/auth/AuthStore";
import { AddAPhoto, Delete } from "@mui/icons-material";
import { roleBadgeSx } from "styles/layouts/TopBar.styles";
import {
  avatar,
  avatarWrapper,
  avatarWrapperContainer,
  deleteButton,
  uploadButton,
} from "styles/components/topBar/UserProfileDialog.styles";
import { uploadPaths } from "utils/uploadUrls";
import FullScreenLoader from "components/loading/FullScreenLoader";

interface Props {
  open: boolean;
  onClose: () => void;
  role?: string;
  borderColor?: string;
}

const UserProfileDialog = observer(
  ({ open, onClose, role, borderColor }: Props) => {
    const user = authStore.user as User;

    const [hovered, setHovered] = useState<boolean>(false);
    const [form, setForm] = useState({
      fullName: "",
      email: "",
      password: "",
      profileImage: null as File | null,
      avatarPreview: "",
    });

    useEffect(() => {
      if (user) {
        setForm({
          fullName: user.fullName,
          email: user.email,
          password: "",
          profileImage: null,
          avatarPreview: user.profileImage
            ? uploadPaths.profilePicture(user.profileImage)
            : "",
        });
      }
    }, [user]);

    const handleChange =
      (key: keyof typeof form) => (e: ChangeEvent<HTMLInputElement>) => {
        setForm({ ...form, [key]: e.target.value });
      };

    const handleAvatarChange = (e: ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        setForm({
          ...form,
          profileImage: file,
          avatarPreview: URL.createObjectURL(file),
        });
      }
    };

    const handleSave = async () => {
      const formData = new FormData();
      formData.append("fullName", form.fullName);
      formData.append("email", form.email);
      if (form.password) formData.append("password", form.password);
      if (form.profileImage) formData.append("profileImage", form.profileImage);
      try {
        await authStore.updateProfile(formData);
      } catch (error) {}
      onClose();
    };

    return (
      <>
        {authStore?.loading && <FullScreenLoader />}
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
          <DialogTitle>Modifier le profil</DialogTitle>
          <DialogContent
            sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}
          >
            <Box sx={avatarWrapperContainer}>
              <Box
                onMouseEnter={() => setHovered(true)}
                onMouseLeave={() => setHovered(false)}
                sx={avatarWrapper}
              >
                <Avatar
                  src={form.avatarPreview || undefined}
                  alt={form.fullName}
                  sx={avatar}
                />
                {role && (
                  <Box
                    sx={{ mt: 1, display: "flex", justifyContent: "center" }}
                  >
                    <Typography
                      variant="caption"
                      sx={roleBadgeSx(borderColor ? borderColor : "gray")}
                    >
                      {role}
                    </Typography>
                  </Box>
                )}
                {!form.avatarPreview && !form.profileImage
                  ? hovered && (
                      <>
                        <input
                          accept="image/*"
                          type="file"
                          id="upload-profile-image"
                          style={{ display: "none" }}
                          onChange={handleAvatarChange}
                        />
                        <label
                          htmlFor="upload-profile-image"
                          style={{ cursor: "pointer" }}
                        >
                          <IconButton
                            className="upload-button"
                            sx={uploadButton}
                            component="span"
                            aria-label="Changer la photo"
                          >
                            <AddAPhoto fontSize="small" />
                          </IconButton>
                        </label>
                      </>
                    )
                  : hovered && (
                      <IconButton
                        onClick={() =>
                          setForm({
                            ...form,
                            profileImage: null,
                            avatarPreview: "",
                          })
                        }
                        sx={deleteButton}
                        aria-label="Supprimer la photo"
                      >
                        <Delete />
                      </IconButton>
                    )}
              </Box>
            </Box>
            <TextField
              label="Nom complet"
              fullWidth
              value={form.fullName}
              onChange={handleChange("fullName")}
            />
            <TextField
              label="Email"
              fullWidth
              value={form.email}
              onChange={handleChange("email")}
            />
            <TextField
              label="Nouveau mot de passe"
              fullWidth
              type="password"
              value={form.password}
              onChange={handleChange("password")}
              helperText="Laissez vide pour ne pas le changer"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Annuler</Button>
            <Button onClick={handleSave} variant="contained">
              Enregistrer
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }
);

export default UserProfileDialog;
